import { defineStore } from 'pinia';
import {Classroom} from "@/models/Classroom.model";
import {useOrganisationsStore} from "@/stores/Organisations.store";
import {User} from "@/models/User.model";
import { Organisation } from '@/models/Organisation.model';
import { usePupilsStore } from "@/stores/Pupils.store";
import { AbstractBaseStore, AbstractStoreState } from "@/stores/AbstractBase.store.ts";
import { ClassroomFilterDef } from "@/stores/filters/ClassroomFilterDef.ts";
import { OrderParameter } from "@/models/OrderParameter.model.ts";

interface ClassroomStoreState extends AbstractStoreState<Classroom, ClassroomFilterDef> {
	currentClassroom: Classroom | null,
}

const BaseStore = AbstractBaseStore<Classroom, ClassroomStoreState, ClassroomFilterDef>();

export const useClassroomsStore = defineStore('classrooms', {

	state: (): ClassroomStoreState => {
		return BaseStore.state({
			order: new OrderParameter('name')
		});
	},

	getters: {

		...BaseStore.getters,

		apiPath(): string {
			return 'classrooms';
		},

		defaultMask(state) {
			return [
				'id',
				'name',
				'pupilCount',
				'teachers.id'
			];
		}

	},

	actions: {

		...BaseStore.actions,

		mapToItem(data: any): Classroom {
			return Classroom.createFromApiData(data);
		},

		new() {
			let newClassroom = new Classroom();
			return newClassroom;
		},

		setDefaultCurrentClassroom(user: User, organisation: Organisation) {

			// Check for query path name, as those are used for deep linking
			const pathName = window.location.pathname;
			const pathNameParts = pathName.split('/');

			let classroomId = null;
			if (pathNameParts.length > 2 && pathNameParts[1] === 'classrooms') {
				classroomId = pathNameParts[2];
			}

			if (!classroomId) {
				// check localstorage
				classroomId = localStorage.getItem('classroom_id');
			}

			if (classroomId) {
				const classroom = organisation.classrooms.find(item => item.id === classroomId);

				if (classroom) {
					this.setCurrentClassroom(classroom);
					return;
				}
			}

			// if that didn't work, set the first one
			if (organisation.classrooms.length > 0) {
				this.setCurrentClassroom(organisation.classrooms[0]);
			} else {
				useOrganisationsStore().setCurrentOrganisation(organisation);
			}
		},

		setCurrentClassroom(classroom: Classroom) {

			if (!classroom.id) {
				throw new Error('Classroom has no id');
			}

			// Store classroom in localstorage
			localStorage.setItem('classroom_id', classroom.id);

			this.currentClassroom = classroom;

			if (this.currentClassroom.organisation) {
				useOrganisationsStore().setCurrentOrganisation(this.currentClassroom.organisation);
			}
		},

		/**
		 * Returns the currently selected classroom or throw an exception if none is set.
		 * This method differs from the state variable 'currentClassroom' as accessing that
		 * does not throw an exception when no classroom is set.
		 * @returns Classroom
		 */
		getCurrentClassroom(): Classroom {
			if (!this.currentClassroom) {
				throw new Error('No current classroom set');
			}
			return this.currentClassroom;
		},

		clearCurrentClassroom() {
			localStorage.removeItem('classroom_id');
			this.currentClassroom = null;
		},

		/**
		 * Switches the current classroom and optionally navigates to a specified location.
		 * @param classroom The classroom to switch to.
		 * @param navigateTo Optional URL to navigate to after switching the classroom.
		 */
		switchClassroom(classroom: Classroom, navigateTo?: string) {
			this.setCurrentClassroom(classroom);

			// using router in Pinia requires another plugin/wrapper, so for the sake of simplicity,
			// using 'window.location' instead
			usePupilsStore().clear();	// otherwise you may notice some strange behaviour while waiting for the page to reload

			// location.reload does NOT work as the classroom id in the route is not updated and takes priority
			// over the value stored in localstorage. So we need to navigate to the same page.
			//window.location.reload();

			if (!classroom.id) {
				throw new Error('Classroom has no id');
			}

			// We always need to reload the page as currentClassroom is used in too many places, and is not
			// always reactive. Refactoring this would be a big job, and frankly, not worth it.
			if (navigateTo) {
				window.location.href = navigateTo;
			} else {
				// Can't seem to access the router so just going to use the location path name.
				const pathName = window.location.pathname;
				const pathNameParts: string[] = pathName.split('/');
				if (pathNameParts[1] === 'classrooms' && pathNameParts.length > 2) {
					pathNameParts[2] = classroom.id;
				}

				window.location.href = pathNameParts.join('/');
			}
		},

		onAddStoreItem(classroom: Classroom) {

			// Also add to the current organisation
			useOrganisationsStore().getCurrentOrganisation().classrooms.push(classroom);

			if (this.items.length === 1) {
				this.setCurrentClassroom(classroom);
			}

		},

		onRemoveStoreItem(classroom: Classroom) {
			if (this.currentClassroom && this.currentClassroom.id == classroom.id) {
				if (this.items.length > 0) {
					this.switchClassroom(this.items[0]);
				} else {
					this.clearCurrentClassroom();
					window.location.reload(); // yucky and hacky, but works.
				}
			}
		}
	}

});
